import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";

import "../shiftplan_v2/styles/base.css";
import "../shiftplan_v2/styles/panels.css";
import useToast from "../../components/useToast";
import { AuthContext } from "../../AuthContext";
import { API_BASE_URL } from "../../config";
import { normalizeTimeInput } from "../../utils/timeInput";
import ProfileSettings from "./ProfileSettings";
import SettingsColorPicker, { SETTINGS_COLOR_PALETTE } from "./SettingsColorPicker";
import TimeOffTypesSection from "./TimeOffTypesSection";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppCheckbox from "../../design-system/components/AppCheckbox";
import AppChip from "../../design-system/components/AppChip";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppEditActions from "../../design-system/components/AppEditActions";
import AppField from "../../design-system/components/AppField";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppRadioGroup from "../../design-system/components/AppRadioGroup";
import AppSelectionList from "../../design-system/components/AppSelectionList";
import AppSelect from "../../design-system/components/AppSelect";
import AppText from "../../design-system/components/AppText";
import AppTooltip from "../../design-system/components/AppTooltip";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import {
  IconAdd,
  IconArchive,
  IconChevronRight,
  IconEdit,
  IconHoliday,
  IconLocation,
  IconProfile,
  IconRoom,
  IconTime,
  IconTimetable,
  IconWarning,
} from "../../design-system/icons";
/*
  Drei Symbole, die das Design-System (noch) nicht fuehrt — deshalb hier lokal
  aus MUI geholt, statt die DS-Liste im Alleingang zu erweitern:
    Unternehmen           Gebaeude statt Weltkugel (die las sich wie "Sprache")
    Abteilungen/Schichten derselbe Gebaeudetyp wie beim Unternehmen, aber als
                          Komplex aus einem grossen und einem kleineren Trakt:
                          eine Abteilung ist ein Teil des Betriebs, kein eigener.
                          Bewusst nah am Unternehmens-Symbol und trotzdem klar
                          unterscheidbar (Fensterbaender statt Fensterraster).
                          Standorte tragen in dieser Software eine Tuer
                          (IconRoom), verwechseln kann man die beiden also nicht.
    Automatische Planung  derselbe Zauberstab wie der Knopf "Automatisch planen"
                          in der Schichtplan-Werkzeugleiste
                          (features/toolbar/ShiftplanV2Toolbar.jsx)
*/
import IconCompany from "@mui/icons-material/ApartmentOutlined";
import IconStructure from "@mui/icons-material/CorporateFareOutlined";
import IconAutoPlanning from "@mui/icons-material/AutoFixHighOutlined";
import { hasAppValueChanged } from "../../design-system/utils/appValueEquality";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./settings.css";

const colorPalette = SETTINGS_COLOR_PALETTE;

let temporaryRowId = 0;

const nextTemporaryRowId = (prefix) => {
  temporaryRowId += 1;
  return `${prefix}-${Date.now()}-${temporaryRowId}`;
};

const emptyDepartment = (index = 0) => ({
  id: null,
  client_id: nextTemporaryRowId("department"),
  name: "",
  shorthand_symbol: "",
  color_hex: colorPalette[index % colorPalette.length],
  is_active: true,
  user_ids: [],
  shift_ids: [],
});

const emptyShift = (index = 0) => ({
  id: null,
  client_id: nextTemporaryRowId("shift"),
  name: "",
  shorthand_symbol: "",
  description: "",
  color_hex: colorPalette[index % colorPalette.length],
  start_time: "",
  end_time: "",
  break_type: "",
  break_duration_minutes: "",
  break_start_time: "",
  break_end_time: "",
  department_ids: [],
  is_active: true,
});

/*
  Die automatische Planung wird derzeit neu gebaut (siehe SOLVER_REWRITE.md).
  Solange die neuen Regeln nicht stehen, wird die alte Konfiguration nicht mehr
  zur Bearbeitung angeboten - Formular, Zustand und Speicherpfad bleiben aber
  erhalten, damit das Wiedereinschalten eine Zeile ist.
*/
const SOLVER_SETTINGS_EDITABLE = false;

const solverRequirementDefaults = {
  desired_min_time_day: 5,
  desired_max_time_day: 6,
  min_time_day: 3,
  max_time_day: 10,
  desired_max_time_week: "",
  max_time_week: 45,
  fair_distribution: "",
  fair_distribution_temp: "woche",
  fair_distribution_weekend: 0,
  week_timeframe: 1,
  subsequent_workingdays: 5,
  subsequent_workingdays_max: 6,
  daily_deployment: 1,
  time_per_deployment: 3,
  new_fte_per_slot: 1,
  skills_per_day: 1,
  perm_always_available: 0,
  min_breaktime_day: 11,
  saving_draft: true,
  ...Array.from({ length: 20 }).reduce((acc, _, index) => {
    const key = `nb${index + 1}`;
    acc[key] = [5, 2, 2, 2, 5, 2, 2, 2, 3, 2, 2, 2, 3, 4, 2, 2, 2, 2, 2, 2][index];
    return acc;
  }, {}),
};

const solverStandardWeights = Array.from({ length: 20 }).reduce((acc, _, index) => {
  const key = `nb${index + 1}`;
  acc[key] = solverRequirementDefaults[key];
  return acc;
}, {});

const solverWorkFields = [
  { key: "min_time_day", label: "Min. Stunden pro Tag", min: 0 },
  { key: "desired_min_time_day", label: "Gewünschte min. Stunden pro Tag", min: 0 },
  { key: "max_time_day", label: "Max. Stunden pro Tag", min: 0 },
  { key: "desired_max_time_day", label: "Gewünschte max. Stunden pro Tag", min: 0 },
  { key: "max_time_week", label: "Max. Stunden pro Woche", min: 0 },
  { key: "desired_max_time_week", label: "Gewünschte max. Stunden pro Woche", min: 0 },
  { key: "fair_distribution", label: "Faire Verteilung", min: 0 },
  { key: "time_per_deployment", label: "Min. Stunden pro Einsatz", min: 0 },
  { key: "new_fte_per_slot", label: "Neue FTE pro Slot", min: 0 },
  { key: "subsequent_workingdays", label: "Gewünschte Arbeitstage in Folge", min: 0, max: 7 },
  { key: "subsequent_workingdays_max", label: "Max. Arbeitstage in Folge", min: 0, max: 14 },
  { key: "min_breaktime_day", label: "Min. Ruhezeit zwischen Tagen", min: 0, max: 24 },
];

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const SHORTHAND_MAX_LENGTH = 4;

const initialWorkspaceSection = () => {
  if (typeof window === "undefined") return "profile";
  return new URLSearchParams(window.location.search).get("section") === "time-off"
    ? "time-off"
    : "profile";
};

const normalizeShorthand = (value) =>
  String(value || "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, SHORTHAND_MAX_LENGTH);

const nameWords = (name) =>
  String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

const shorthandCandidates = (name) => {
  const words = nameWords(name);
  if (!words.length) return [];

  const candidates = new Set();
  const initials = words.map((word) => word[0]).join("");
  candidates.add(initials);

  const firstWord = words[0];
  const remainingInitials = words.slice(1).map((word) => word[0]).join("");
  for (let length = 2; length <= Math.min(firstWord.length, SHORTHAND_MAX_LENGTH); length += 1) {
    candidates.add(`${firstWord.slice(0, length)}${remainingInitials}`);
  }

  candidates.add(words.join("").slice(0, SHORTHAND_MAX_LENGTH));
  return Array.from(candidates).map(normalizeShorthand).filter(Boolean);
};

const generateUniqueShorthand = (name, existingSymbols) => {
  const used = new Set(existingSymbols.map((value) => normalizeShorthand(value)).filter(Boolean));
  const candidates = shorthandCandidates(name);

  for (const candidate of candidates) {
    if (!used.has(candidate)) return candidate;
  }

  const fallbackBase = candidates[0] || "X";
  for (let number = 2; number < 1000; number += 1) {
    const suffix = String(number);
    const candidate = `${fallbackBase.slice(0, SHORTHAND_MAX_LENGTH - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }

  return fallbackBase.slice(0, SHORTHAND_MAX_LENGTH);
};

const autoShorthandForName = (items, index, name) => {
  const existingSymbols = items
    .filter((_, itemIndex) => itemIndex !== index)
    .map((item) => item.shorthand_symbol);
  return generateUniqueShorthand(name, existingSymbols);
};

const shiftBreakLabel = (shift, t) => {
  if (shift.break_type === "fixed" && shift.break_start_time && shift.break_end_time) {
    return t("settings_ui.shift_break_fixed", {
      start: shift.break_start_time,
      end: shift.break_end_time,
    });
  }
  const minutes = Number(shift.break_duration_minutes);
  return Number.isFinite(minutes) && minutes > 0
    ? t("settings_ui.shift_break_minutes", { count: minutes })
    : t("settings_ui.shift_no_break");
};

const shiftScheduleLabel = (shift, t) => {
  const time = shift.start_time && shift.end_time
    ? `${shift.start_time}–${shift.end_time}`
    : t("settings_ui.shift_time_pending");
  return `${time} · ${shiftBreakLabel(shift, t)}`;
};

const assignedShiftIds = (department, shifts) => {
  if (Array.isArray(department.shift_ids)) return department.shift_ids;
  return (shifts || [])
    .filter((shift) => (shift.department_ids || []).includes(department.id))
    .map((shift) => shift.id);
};

const stripClientFields = (items) =>
  (items || []).map(({ client_id, shorthand_manual, ...item }) => item);

const nextColor = (items) => {
  const used = new Set(items.map((item) => item.color_hex).filter(Boolean));
  return colorPalette.find((color) => !used.has(color)) || colorPalette[items.length % colorPalette.length];
};

const InputLabel = ({ children }) => children;

const optionText = (node) => {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (!React.isValidElement(node) || node.type === Checkbox) return "";
  return React.Children.toArray(node.props.children).map(optionText).filter(Boolean).join(" ");
};

const Select = ({ children, value, onChange, multiple, disabled, labelId, label, input, renderValue }) => {
  const { t } = useTranslation();
  void labelId;
  void label;
  void input;
  void renderValue;
  const options = React.Children.toArray(children).map((child) => ({
    value: child.props.value,
    label: optionText(child).trim() || String(child.props.value ?? ""),
  }));
  return (
    <AppSelect
      options={options}
      value={value}
      onChange={onChange}
      multiple={Boolean(multiple)}
      searchable={Boolean(multiple)}
      disabled={disabled}
      placeholder={t("settings_ui.select")}
      searchPlaceholder={t("settings_ui.search_option")}
    />
  );
};

const FormControl = ({ children, disabled, fullWidth, sx }) => {
  void fullWidth;
  void sx;
  const items = React.Children.toArray(children);
  const labelNode = items.find((child) => React.isValidElement(child) && child.type === InputLabel);
  const selectNode = items.find((child) => React.isValidElement(child) && child.type === Select);
  if (!selectNode) return <>{children}</>;
  return (
    <AppField label={labelNode?.props.children || selectNode.props.label}>
      {React.cloneElement(selectNode, { disabled: disabled || selectNode.props.disabled })}
    </AppField>
  );
};

const Button = ({ variant, color, size, startIcon, endIcon, sx, children, ...props }) => {
  void sx;
  const appVariant = color === "error" || color === "warning"
    ? "error"
    : variant === "contained"
    ? "primary"
    : variant === "outlined"
    ? "outlined"
    : "muted";
  return (
    <AppButton variant={appVariant} size={size === "small" ? "sm" : "md"} {...props}>
      {startIcon}
      {children}
      {endIcon}
    </AppButton>
  );
};

const Checkbox = ({ sx, ...props }) => {
  void sx;
  return <AppCheckbox {...props} />;
};

const Alert = ({ severity = "info", children, sx }) => {
  void sx;
  return <AppCallout variant={severity} description={children} />;
};

const CompanyCard = ({ icon, title, onManage, hidden = false, children }) => {
  const { t } = useTranslation();
  if (hidden) return null;
  return (
    <AppCard>
      <AppCardHeader
        title={title}
        visual={icon}
        action={
          <div className="company-section-view__actions">
            {onManage ? (
              <AppTooltip content={t("settings_ui.edit_named", { name: title })}>
                <AppButton variant="muted" size="md" iconOnly onClick={onManage} aria-label={t("settings_ui.edit_named", { name: title })}>
                  <IconEdit />
                </AppButton>
              </AppTooltip>
            ) : null}
          </div>
        }
      />
      <AppCardBody>{children}</AppCardBody>
    </AppCard>
  );
};

const ConfigStatusItem = ({ icon, title, detail, recommended }) => (
  <div className={`company-summary__item${recommended ? " company-summary__item--warning" : ""}`}>
    <span className="company-summary__item-icon" aria-hidden="true">{icon}</span>
    <span className="company-summary__item-copy">
      <span className="company-summary__item-title">{title}</span>
      <span className="company-summary__item-detail">{detail}</span>
    </span>
  </div>
);

const EditPanel = ({ title, subtitle, open, onClose, onSave, saving, dirty, saveLabel, children }) => {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <AppCard id="company-edit-panel">
      <AppCardHeader
        title={title}
        subtitle={subtitle}
        action={
          <div className="company-section-view__actions">
            <AppEditActions
              editing
              dirty={dirty}
              saving={saving}
              onCancel={onClose}
              onSave={onSave}
              cancelLabel={t("settings_ui.cancel_edit")}
              saveLabel={saveLabel || t("settings_ui.save_changes")}
              savingLabel={t("settings_ui.saving_changes")}
            />
          </div>
        }
      />
      <AppCardBody>{children}</AppCardBody>
    </AppCard>
  );
};

const ReadField = ({ label, value }) => (
  <AppField label={label}>
    <AppInput value={value ?? "-"} readOnly />
  </AppField>
);

const INACTIVE_COPY = {
  departments: {
    title: "Abteilung deaktivieren?",
    label: "Abteilung",
    message:
      "Die Abteilung ist danach nicht mehr auswählbar. Sie wird aus Schicht-Zuordnungen entfernt, Standardbedarf-Einträge werden gelöscht und zukünftige Bedarfswerte werden entfernt.",
  },
  shifts: {
    title: "Schicht deaktivieren?",
    label: "Schicht",
    message:
      "Die Schicht ist danach nicht mehr auswählbar. Sie wird aus Abteilungs-Zuordnungen entfernt, Standardbedarf-Einträge werden gelöscht und zukünftige Bedarfswerte werden entfernt.",
  },
};

export default function Settings() {
  const { t } = useTranslation();
  const token = localStorage.getItem("session_token");
  const { success, error } = useToast();
  const { user, locations, selectedLocationId, syncLocations } = useContext(AuthContext);
  const accessLevel = user?.accessLevel || user?.access_level;
  const canManageCompany = ["Super_Admin", "Admin", "company_admin"].includes(accessLevel);
  const isSuperAdmin = accessLevel === "Super_Admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [activeSection, setActiveSection] = useState(initialWorkspaceSection);
  const [drawer, setDrawer] = useState(null);
  const [departmentEditIndex, setDepartmentEditIndex] = useState(null);
  const [shiftEditIndex, setShiftEditIndex] = useState(null);
  // Abteilungen und Schichten teilen sich eine Ansicht und damit ein Archiv.
  const [structureArchiveOpen, setStructureArchiveOpen] = useState(false);
  const [form, setForm] = useState({});
  const [initialForm, setInitialForm] = useState({});
  const [formError, setFormError] = useState("");
  const [inactiveConfirm, setInactiveConfirm] = useState(null);
  // Wird ein Öffnungstag wieder geöffnet, fragt dieser Dialog sofort nach, wer
  // an dem Tag künftig verfügbar ist.
  const [openingDayDialog, setOpeningDayDialog] = useState(null);

  // Abteilungen, Schichten und Öffnungstage haengen am gewaehlten Standort — der
  // gehoert deshalb sichtbar an den Seitentitel. Gibt es nur einen Standort, ist
  // die Angabe ohne Aussage und bleibt weg; der Super_Admin sieht sie wie in der
  // Topbar immer.
  const activeLocationName = useMemo(() => {
    const items = locations || [];
    if (items.length < 2 && !isSuperAdmin) return "";
    const active = items.find((item) => String(item.id) === String(selectedLocationId));
    return (active || items[0])?.name || "";
  }, [locations, selectedLocationId, isSuperAdmin]);

  const openDays = useMemo(
    () => (company?.opening_days || []).filter((day) => day.is_open),
    [company]
  );
  const currentUsers = useMemo(() => company?.users || [], [company]);
  const activeDepartmentOptions = useMemo(
    () => (company?.departments || []).filter((department) => department.is_active !== false),
    [company]
  );
  const canEditCompanySettings = Boolean(company?.can_edit_company_settings);
  const solverRequirement = {
    ...solverRequirementDefaults,
    ...(company?.solver_requirement || {}),
  };
  const solverStandardSelected = Object.entries(solverStandardWeights).every(
    ([key, value]) => String(form.solver_requirement?.[key] ?? "") === String(value)
  );
  const activeLocations = (company?.locations || []).filter((location) => location.is_active);
  const activeDepartments = (company?.departments || []).filter((department) => department.is_active !== false);
  const activeShifts = (company?.shifts || []).filter((shift) => shift.is_active !== false);
  const departmentRows = (company?.departments || []).map((department, index) => ({ department, index }));
  const archivedDepartmentCount = departmentRows.filter(({ department }) => department.is_active === false).length;
  const visibleDepartmentRows = departmentRows.filter(
    ({ department }) => department.is_active !== false || structureArchiveOpen
  );
  const shiftRows = (company?.shifts || []).map((shift, index) => ({ shift, index }));
  const archivedShiftCount = shiftRows.filter(({ shift }) => shift.is_active === false).length;
  const visibleShiftRows = shiftRows.filter(
    ({ shift }) => shift.is_active !== false || structureArchiveOpen
  );
  // Schichten ohne Abteilung tauchen in keiner Gruppe auf und bekommen darum
  // einen eigenen Block, sonst waeren sie in der Ansicht unsichtbar.
  const unassignedShiftRows = visibleShiftRows.filter(
    ({ shift }) => !(shift.department_ids || []).length
  );
  const solverProfileDefined = Object.keys(solverStandardWeights).every((key) => {
    const value = solverRequirement?.[key];
    return value !== undefined && value !== null && value !== "";
  });
  const generalComplete = Boolean(company?.company_name && company?.weekly_hours && company?.working_days_per_week);
  // Gleiche Reihenfolge wie die Kacheln im Konfigurationsstatus — fuer den
  // Prozentwert egal, beim Lesen aber deutlich einfacher zu vergleichen.
  const statusSections = [
    generalComplete,
    activeLocations.length > 0,
    activeDepartments.length > 0,
    activeShifts.length > 0,
    openDays.length > 0,
    solverProfileDefined,
  ];
  const configurationPercent = Math.round(
    (statusSections.filter(Boolean).length / statusSections.length) * 100
  );
  const configurationCopy = configurationPercent === 100
    ? {
        title: t("settings_ui.configuration_complete_title"),
        description: t("settings_ui.configuration_complete_description"),
      }
    : configurationPercent >= 80
      ? {
          title: t("settings_ui.configuration_almost_complete_title"),
          description: t("settings_ui.configuration_almost_complete"),
        }
      : configurationPercent >= 60
        ? {
            title: t("settings_ui.configuration_in_progress_title"),
            description: t("settings_ui.configuration_in_progress_description"),
          }
        : {
            title: t("settings_ui.configuration_recommended_title"),
            description: t("settings_ui.configuration_recommended_description"),
          };
  const workspaceSections = [
    {
      key: "profile",
      label: "Profil",
      description: "Persönliche Daten",
      icon: <IconProfile />,
      color: "var(--ds-color-brand-default-strong)",
      disabled: false,
    },
    {
      key: "general",
      label: "Unternehmen",
      description: "Basisdaten",
      icon: <IconCompany />,
      color: "#059669",
      disabled: !canEditCompanySettings,
    },
    {
      key: "structure",
      label: t("settings_ui.departments_and_shifts"),
      description: t("settings_ui.structure_navigation_summary", {
        departments: activeDepartments.length,
        shifts: activeShifts.length,
      }),
      icon: <IconStructure />,
      color: "#2563EB",
      disabled: !canEditCompanySettings,
    },
    {
      key: "time-off",
      label: t("settings_ui.time_off_types"),
      description: t("settings_ui.time_off_navigation_description"),
      icon: <IconHoliday />,
      color: "#7C3AED",
      disabled: !canEditCompanySettings,
    },
    {
      key: "opening",
      label: "Öffnungstage",
      description: `${openDays.length} / 7 aktiv`,
      icon: <IconTimetable />,
      color: "#059669",
      disabled: !canEditCompanySettings,
    },
    {
      key: "solver",
      label: "Automatische Planung",
      description: SOLVER_SETTINGS_EDITABLE
        ? (solverProfileDefined ? "Regeln gesetzt" : "Regeln noch offen")
        : "Wird überarbeitet",
      icon: <IconAutoPlanning />,
      color: "#0EA5E9",
      disabled: !canEditCompanySettings,
    },
  ];
  const fetchCompany = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/web/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompany(res.data || {});
      syncLocations(res.data?.locations || [], res.data?.location?.id);
    } catch (err) {
      error("Unternehmensdaten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageCompany) {
      setLoading(false);
      return;
    }
    fetchCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageCompany, selectedLocationId]);

  useEffect(() => {
    if (!drawer) return;
    requestAnimationFrame(() => {
      document.getElementById("company-edit-panel")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [drawer]);

  const openDrawer = (name) => {
    setFormError("");
    let nextForm = {};
    if (name === "general") {
      nextForm = {
        company_name: company?.company_name || "",
        weekly_hours: company?.weekly_hours ?? "",
        working_days_per_week: company?.working_days_per_week ?? "",
      };
    }
    if (name === "departments") {
      nextForm = {
        departments: (company?.departments || []).map((department) => ({
          ...department,
          shift_ids: assignedShiftIds(department, company?.shifts),
        })),
      };
    }
    if (name === "opening") {
      nextForm = {
        opening_days: [...(company?.opening_days || [])],
        // Wochentag → Mitarbeitende, die dort nach dem Öffnen verfügbar sind.
        opening_availability: {},
      };
    }
    if (name === "shifts") {
      const activeDepartmentIds = new Set(activeDepartmentOptions.map((department) => department.id));
      nextForm = {
        shifts: (company?.shifts || []).map((shift) => ({
          ...shift,
          department_ids: (shift.department_ids || []).filter((departmentId) =>
            activeDepartmentIds.has(departmentId)
          ),
        })),
      };
    }
    if (name === "solver") {
      nextForm = {
        solver_requirement: {
          ...solverRequirementDefaults,
          ...(company?.solver_requirement || {}),
        },
      };
    }
    setForm(nextForm);
    setInitialForm(nextForm);
    setDrawer(name);
  };

  const closeDrawer = () => {
    setDrawer(null);
    setDepartmentEditIndex(null);
    setShiftEditIndex(null);
    setForm({});
    setInitialForm({});
    setFormError("");
    setInactiveConfirm(null);
    setOpeningDayDialog(null);
  };

  const weekdayNames = t("availability.weekdays.full", { returnObjects: true });
  const weekdayName = (weekday) =>
    weekdayNames?.[weekday]
    || (form.opening_days || []).find((item) => item.weekday === weekday)?.label
    || "";

  // Was sich gegenüber dem geladenen Stand geändert hat — daraus ergibt sich,
  // welche Standardverfügbarkeiten mitgezogen werden.
  const openingWeekdayChanges = (fromOpen, toOpen) =>
    (form.opening_days || [])
      .filter((day) => {
        const before = (initialForm.opening_days || []).find((item) => item.weekday === day.weekday);
        return Boolean(before?.is_open) === fromOpen && Boolean(day.is_open) === toOpen;
      })
      .map((day) => day.weekday);
  const closedOpeningWeekdays = drawer === "opening" ? openingWeekdayChanges(true, false) : [];
  const reopenedOpeningWeekdays = drawer === "opening" ? openingWeekdayChanges(false, true) : [];

  // Vorschlag für den Dialog: wer diesen Wochentag heute schon als Arbeitstag
  // führt. Ist das niemand — etwa weil der Tag lange zu war — steht die ganze
  // Belegschaft zur Auswahl.
  const suggestedAvailableUserIds = (weekday) => {
    const matching = currentUsers
      .filter((item) => (item.default_weekdays || []).includes(weekday))
      .map((item) => item.id);
    return matching.length ? matching : currentUsers.map((item) => item.id);
  };

  const openOpeningDayDialog = (weekday, { revertOnCancel = false } = {}) => {
    setOpeningDayDialog({
      weekday,
      revertOnCancel,
      userIds: form.opening_availability?.[weekday] || suggestedAvailableUserIds(weekday),
    });
  };

  const toggleOpeningDay = (index) => {
    const day = (form.opening_days || [])[index];
    if (!day) return;
    const nextOpen = !day.is_open;
    setForm((current) => ({
      ...current,
      opening_days: (current.opening_days || []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, is_open: nextOpen } : item
      ),
      // Ein wieder geschlossener Tag braucht keine Auswahl mehr.
      opening_availability: nextOpen
        ? current.opening_availability || {}
        : Object.fromEntries(
          Object.entries(current.opening_availability || {})
            .filter(([weekday]) => Number(weekday) !== day.weekday)
        ),
    }));
    if (nextOpen) {
      openOpeningDayDialog(day.weekday, { revertOnCancel: true });
    }
  };

  const closeOpeningDayDialog = () => {
    if (openingDayDialog?.revertOnCancel) {
      const weekday = openingDayDialog.weekday;
      setForm((current) => ({
        ...current,
        opening_days: (current.opening_days || []).map((item) =>
          item.weekday === weekday ? { ...item, is_open: false } : item
        ),
      }));
    }
    setOpeningDayDialog(null);
  };

  const confirmOpeningDayDialog = () => {
    if (!openingDayDialog) return;
    const { weekday, userIds } = openingDayDialog;
    setForm((current) => ({
      ...current,
      opening_availability: { ...(current.opening_availability || {}), [weekday]: userIds },
    }));
    setOpeningDayDialog(null);
  };

  const toggleOpeningDayUser = (userId) => {
    setOpeningDayDialog((current) => {
      if (!current) return current;
      return {
        ...current,
        userIds: current.userIds.includes(userId)
          ? current.userIds.filter((item) => item !== userId)
          : [...current.userIds, userId],
      };
    });
  };

  const selectWorkspaceSection = (sectionKey) => {
    closeDrawer();
    setActiveSection(sectionKey);
  };

  const openDepartmentEditor = (index) => {
    openDrawer("departments");
    setDepartmentEditIndex(index);
  };

  const openNewDepartmentEditor = () => {
    const departments = [
      ...(company?.departments || []),
      { ...emptyDepartment(company?.departments?.length || 0), color_hex: nextColor(company?.departments || []) },
    ];
    const nextForm = { departments };
    setForm(nextForm);
    setInitialForm(nextForm);
    setFormError("");
    setDepartmentEditIndex(departments.length - 1);
    setDrawer("departments");
  };

  const openShiftEditor = (index) => {
    openDrawer("shifts");
    setShiftEditIndex(index);
  };

  const openNewShiftEditor = () => {
    const shifts = [
      ...(company?.shifts || []),
      { ...emptyShift(company?.shifts?.length || 0), color_hex: nextColor(company?.shifts || []) },
    ];
    const nextForm = { shifts };
    setForm(nextForm);
    setInitialForm(nextForm);
    setFormError("");
    setShiftEditIndex(shifts.length - 1);
    setDrawer("shifts");
  };

  const updateFormRow = (type, index, changes) => {
    setForm((current) => ({
      ...current,
      [type]: (current[type] || []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...changes } : item
      ),
    }));
  };

  const applyInactiveRow = (type, index) => {
    const cleanup =
      type === "departments"
        ? { is_active: false, user_ids: [], shift_ids: [] }
        : type === "shifts"
        ? { is_active: false, department_ids: [] }
        : { is_active: false };
    updateFormRow(type, index, cleanup);
  };

  const requestActiveChange = (type, index, checked) => {
    if (checked) {
      updateFormRow(type, index, { is_active: true });
      return;
    }
    const item = form[type]?.[index];
    if (!item?.id) {
      applyInactiveRow(type, index);
      return;
    }
    setInactiveConfirm({
      type,
      index,
      name: item.name || INACTIVE_COPY[type]?.label || "Eintrag",
    });
  };

  const confirmInactive = () => {
    if (!inactiveConfirm) return;
    applyInactiveRow(inactiveConfirm.type, inactiveConfirm.index);
    setInactiveConfirm(null);
  };

  const saveSection = async () => {
    if (!hasAppValueChanged(initialForm, form)) return;
    const shiftBeingEdited = Number.isInteger(shiftEditIndex)
      ? form.shifts?.[shiftEditIndex]
      : null;
    if (
      drawer === "shifts" &&
      shiftBeingEdited &&
      shiftBeingEdited?.is_active !== false &&
      !(shiftBeingEdited?.department_ids || []).length
    ) {
      setFormError(t("settings_ui.active_shift_department_required"));
      return;
    }
    if (
      drawer === "shifts" &&
      shiftBeingEdited?.break_type === "fixed" &&
      (!TIME_RE.test(shiftBeingEdited.break_start_time || "") || !TIME_RE.test(shiftBeingEdited.break_end_time || ""))
    ) {
      setFormError(t("settings_ui.break_fixed_times_required"));
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      if (drawer === "general") {
        await axios.put(`${API_BASE_URL}/api/web/settings/general`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (drawer === "departments") {
        await axios.put(`${API_BASE_URL}/api/web/settings/departments`, stripClientFields(form.departments), {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (drawer === "opening") {
        await axios.put(
          `${API_BASE_URL}/api/web/settings/opening-days`,
          {
            days: form.opening_days || [],
            // Nur wieder geöffnete Tage tragen eine Auswahl; geschlossene Tage
            // nimmt das Backend allen Mitarbeitenden von selbst weg.
            availability: Object.fromEntries(
              reopenedOpeningWeekdays.map((weekday) => [
                weekday,
                form.opening_availability?.[weekday] || suggestedAvailableUserIds(weekday),
              ])
            ),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      if (drawer === "shifts") {
        await axios.put(`${API_BASE_URL}/api/web/settings/shifts`, stripClientFields(form.shifts), {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (drawer === "solver") {
        await axios.put(`${API_BASE_URL}/api/web/settings/solver-requirement`, form.solver_requirement || {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      success("Änderungen gespeichert.");
      closeDrawer();
      await fetchCompany();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Änderungen konnten nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const updateDepartment = (index, key, value) => {
    const nextValue = key === "user_ids" || key === "shift_ids"
      ? (value || []).map(Number)
      : value;
    setForm((current) => ({
      ...current,
      departments: (current.departments || []).map((department, itemIndex) => {
        if (itemIndex !== index) return department;
        if (key === "name") {
          return {
            ...department,
            name: nextValue,
            shorthand_symbol: department.shorthand_manual
              ? department.shorthand_symbol
              : autoShorthandForName(current.departments || [], index, nextValue),
          };
        }
        if (key === "shorthand_symbol") {
          return {
            ...department,
            shorthand_symbol: normalizeShorthand(nextValue),
            shorthand_manual: true,
          };
        }
        return { ...department, [key]: nextValue };
      }),
    }));
  };

  const updateShift = (index, key, value) => {
    const nextValue =
      key === "start_time" || key === "end_time"
        ? normalizeTimeInput(value)
        : key === "department_ids"
        ? (value || []).map(Number)
        : key === "shorthand_symbol"
        ? normalizeShorthand(value)
        : value;
    setForm((current) => ({
      ...current,
      shifts: (current.shifts || []).map((shift, itemIndex) => {
        if (itemIndex !== index) return shift;
        if (key === "name") {
          return {
            ...shift,
            name: nextValue,
            shorthand_symbol: shift.shorthand_manual
              ? shift.shorthand_symbol
              : autoShorthandForName(current.shifts || [], index, nextValue),
          };
        }
        if (key === "shorthand_symbol") {
          return {
            ...shift,
            shorthand_symbol: normalizeShorthand(nextValue),
            shorthand_manual: true,
          };
        }
        return { ...shift, [key]: nextValue };
      }),
    }));
  };

  const updateSolverRequirement = (key, value) => {
    setForm((current) => ({
      ...current,
      solver_requirement: {
        ...solverRequirementDefaults,
        ...(current.solver_requirement || {}),
        [key]: value,
      },
    }));
  };

  const applySolverProfile = (profile) => {
    if (profile !== "standard") return;
    setForm((current) => ({
      ...current,
      solver_requirement: {
        ...solverRequirementDefaults,
        ...(current.solver_requirement || {}),
        ...solverStandardWeights,
      },
    }));
  };

  const solverNumberField = ({ key, label, min = 0, max, step = 1 }) => (
    <AppField key={key} label={label}>
      <AppInput
        type="number"
        value={form.solver_requirement?.[key] ?? ""}
        onChange={(event) => updateSolverRequirement(key, event.target.value)}
        min={min}
        max={max}
        step={step}
      />
    </AppField>
  );
  const editedDepartment = Number.isInteger(departmentEditIndex)
    ? form.departments?.[departmentEditIndex]
    : null;
  const editedShift = Number.isInteger(shiftEditIndex)
    ? form.shifts?.[shiftEditIndex]
    : null;
  const isNewDepartment = Boolean(editedDepartment && !editedDepartment.id);
  const isNewShift = Boolean(editedShift && !editedShift.id);

  if (!canManageCompany) {
    return (
      <div className="company-page">
        <AppWorkspace title={t("settings_ui.settings")}>
          <div className="company-workspace">
            <aside className="company-workspace__sidebar" aria-label={t("settings_ui.settings")}>
              <nav className="company-workspace__nav">
                <button type="button" className="company-workspace__nav-item is-active">
                  <span className="company-workspace__nav-icon" aria-hidden="true"><IconProfile /></span>
                  <span className="company-workspace__nav-copy">
                    <strong>{t("settings_ui.profile")}</strong>
                    <small>{t("settings_ui.personal_data")}</small>
                  </span>
                </button>
              </nav>
            </aside>
            <main className="company-workspace__editor">
              <div className="company-section-view"><ProfileSettings /></div>
            </main>
          </div>
        </AppWorkspace>
      </div>
    );
  }

  const workspaceTitle = (
    <>
      {t("settings_ui.settings")}
      {activeLocationName ? (
        <span className="company-page__location">
          <IconLocation aria-hidden="true" />
          <span className="company-page__location-label">
            {t("settings_ui.location_context", "Standort")}
          </span>
          <span className="company-page__location-name">{activeLocationName}</span>
        </span>
      ) : null}
    </>
  );

  return (
    <div className="company-page">
      <AppWorkspace title={workspaceTitle} loading={loading}>

      {company && (
        <>
          <section className="company-summary" aria-labelledby="company-summary-title">
            <h2 id="company-summary-title" className="company-summary__title">{t("settings_ui.configuration_status")}</h2>
            <div className="company-summary__body">
              <div className="company-summary__progress-block">
                <div
                  className="company-summary__progress"
                  style={{ "--company-progress": `${configurationPercent * 3.6}deg` }}
                  aria-label={`${configurationPercent} Prozent konfiguriert`}
                >
                  <span>{configurationPercent}%</span>
                </div>
                <div className="company-summary__progress-copy">
                  <strong>{configurationCopy.title}</strong>
                  <span>{configurationCopy.description}</span>
                </div>
              </div>
              <div className="company-summary__divider" aria-hidden="true" />
              {/* Reihenfolge und Symbole folgen den Bereichen unten in der
                  Seitenleiste: Unternehmen (mit Standorten), dann Abteilungen und
                  Schichten als Paar, dann Öffnungstage, dann die Planung. */}
              <div className="company-summary__items">
                <ConfigStatusItem
                  icon={generalComplete ? <IconCompany /> : <IconWarning />}
                  title={t("settings_ui.general_settings")}
                  detail={generalComplete
                    ? t("settings_ui.configuration_status_complete_detail")
                    : t("settings_ui.configuration_status_recommended_detail")}
                  recommended={!generalComplete}
                />
                <ConfigStatusItem
                  icon={activeLocations.length ? <IconRoom /> : <IconWarning />}
                  title={t("settings_ui.locations")}
                  detail={activeLocations.length
                    ? t("settings_ui.configuration_status_active_count", { count: activeLocations.length })
                    : t("settings_ui.configuration_status_recommended_detail")}
                  recommended={!activeLocations.length}
                />
                <ConfigStatusItem
                  icon={activeDepartments.length ? <IconStructure /> : <IconWarning />}
                  title={t("settings_ui.departments")}
                  detail={activeDepartments.length
                    ? t("settings_ui.configuration_status_active_count", { count: activeDepartments.length })
                    : t("settings_ui.configuration_status_recommended_detail")}
                  recommended={!activeDepartments.length}
                />
                <ConfigStatusItem
                  icon={activeShifts.length ? <IconTime /> : <IconWarning />}
                  title={t("settings_ui.shifts")}
                  detail={activeShifts.length
                    ? t("settings_ui.configuration_status_active_count", { count: activeShifts.length })
                    : t("settings_ui.configuration_status_recommended_detail")}
                  recommended={!activeShifts.length}
                />
                <ConfigStatusItem
                  icon={openDays.length ? <IconTimetable /> : <IconWarning />}
                  title={t("settings_ui.opening_days")}
                  detail={openDays.length
                    ? t("settings_ui.configuration_status_active_days", { count: openDays.length })
                    : t("settings_ui.configuration_status_recommended_detail")}
                  recommended={!openDays.length}
                />
                {/* Solange nichts einstellbar ist, keine Empfehlung aussprechen -
                    sonst mahnt die Kachel etwas an, das man gar nicht tun kann. */}
                <ConfigStatusItem
                  icon={!SOLVER_SETTINGS_EDITABLE || solverProfileDefined ? <IconAutoPlanning /> : <IconWarning />}
                  title={t("settings_ui.solver_requirements")}
                  detail={!SOLVER_SETTINGS_EDITABLE
                    ? t("settings_ui.configuration_status_solver_maintenance")
                    : solverProfileDefined
                      ? t("settings_ui.configuration_status_solver_defined")
                      : t("settings_ui.configuration_status_recommended_detail")}
                  recommended={SOLVER_SETTINGS_EDITABLE && !solverProfileDefined}
                />
              </div>
            </div>
          </section>

          <div className="company-workspace">
            <aside className="company-workspace__sidebar" aria-label={t("settings_ui.company_settings")}>
              <nav className="company-workspace__nav">
                {workspaceSections.map((section) => (
                  <button
                    key={section.key}
                    type="button"
                    disabled={section.disabled}
                    className={`company-workspace__nav-item${activeSection === section.key ? " is-active" : ""}`}
                    style={{ "--company-section-color": section.color }}
                    onClick={() => selectWorkspaceSection(section.key)}
                  >
                    <span className="company-workspace__nav-icon" aria-hidden="true">{section.icon}</span>
                    <span className="company-workspace__nav-copy">
                      <strong>{section.label}</strong>
                      <small>{section.description}</small>
                    </span>
                  </button>
                ))}
              </nav>
            </aside>
            <main className="company-workspace__editor">
          {!drawer && (
          <div className="company-section-view">
            {activeSection === "profile" ? <ProfileSettings /> : null}

            <CompanyCard
              hidden={activeSection !== "general"}
              title={t("settings_ui.general_settings")}
              icon={<IconCompany />}
              color="#059669"
              onManage={canEditCompanySettings ? () => openDrawer("general") : undefined}
              actionColor="#059669"
            >
              <AppFieldGrid columns={3}>
                <ReadField label={t("settings_ui.company_name")} value={company.company_name || "-"} />
                <ReadField label={t("settings_ui.weekly_hours")} value={`${company.weekly_hours || "-"} h`} />
                <ReadField label={t("settings_ui.working_days_week")} value={`${company.working_days_per_week || "-"} ${t("settings_ui.days")}`} />
              </AppFieldGrid>
            </CompanyCard>


            {activeSection === "structure" ? (
              <AppCard>
                <AppCardHeader
                  title={t("settings_ui.departments_and_shifts")}
                  subtitle={t("settings_ui.structure_subtitle")}
                  visual={<IconStructure />}
                  action={canEditCompanySettings ? (
                    <div className="company-section-view__actions">
                      <AppButton
                        variant="muted"
                        size="sm"
                        onClick={() => setStructureArchiveOpen((current) => !current)}
                        aria-expanded={structureArchiveOpen}
                      >
                        <IconArchive />
                        {structureArchiveOpen
                          ? t("settings_ui.hide_structure_archive")
                          : t("settings_ui.structure_archive_count", { count: archivedDepartmentCount + archivedShiftCount })}
                      </AppButton>
                      <AppButton variant="outlined" size="sm" onClick={openNewShiftEditor}>
                        <IconAdd />
                        {t("settings_ui.add_shift")}
                      </AppButton>
                      <AppButton variant="primary" size="sm" onClick={openNewDepartmentEditor}>
                        <IconAdd />
                        {t("settings_ui.add_department")}
                      </AppButton>
                    </div>
                  ) : null}
                />
                <AppCardBody>
                  <div className="company-structure">
                    <div className="company-structure__guide" role="note" aria-label={t("settings_ui.structure_guide_label")}>
                      <span className="company-structure__guide-item">
                        <span className="company-structure__guide-icon" aria-hidden="true"><IconStructure /></span>
                        <span>
                          <strong>{t("settings_ui.department")}</strong>
                          <small>{t("settings_ui.structure_department_description")}</small>
                        </span>
                      </span>
                      <IconChevronRight className="company-structure__guide-arrow" aria-hidden="true" />
                      <span className="company-structure__guide-item">
                        <span className="company-structure__guide-icon company-structure__guide-icon--shift" aria-hidden="true"><IconTime /></span>
                        <span>
                          <strong>{t("settings_ui.shift")}</strong>
                          <small>{t("settings_ui.structure_shift_description")}</small>
                        </span>
                      </span>
                      <AppText as="p" variant="body-2" tone="secondary">
                        {t("settings_ui.structure_multi_department_note")}
                      </AppText>
                    </div>

                    {visibleDepartmentRows.length ? (
                      visibleDepartmentRows.map(({ department, index }) => {
                        const departmentShifts = visibleShiftRows.filter(({ shift }) =>
                          (shift.department_ids || []).includes(department.id)
                        );
                        const memberCount = (department.user_ids || []).length;

                        return (
                          <section
                            className={`company-structure__group${department.is_active === false ? " is-inactive" : ""}`}
                            key={department.id || department.client_id || index}
                            style={{ "--company-department-accent": department.color_hex || "var(--ds-color-brand-default)" }}
                          >
                            <header className="company-structure__group-header">
                              <AppChip
                                variant="primary"
                                appearance="outlined"
                                size="md"
                                className="company-structure__abbreviation-chip company-structure__abbreviation-chip--department"
                                style={{ "--company-entity-accent": department.color_hex || "var(--ds-color-brand-default)" }}
                              >
                                {department.shorthand_symbol || "—"}
                              </AppChip>
                              <div className="company-structure__group-copy">
                                <span className="company-structure__kind">{t("settings_ui.department")}</span>
                                <strong>{department.name || t("settings_ui.unnamed_department")}</strong>
                                <span>
                                  {t("settings_ui.structure_summary", { employees: memberCount, shifts: departmentShifts.length })}
                                </span>
                              </div>
                              <div className="company-structure__group-actions">
                                {department.is_active === false ? (
                                  <AppChip variant="muted" appearance="outlined" size="sm">
                                    {t("settings_ui.inactive")}
                                  </AppChip>
                                ) : null}
                                {canEditCompanySettings ? (
                                  <AppButton
                                    variant="outlined"
                                    size="sm"
                                    aria-label={t("settings_ui.edit_department_aria", { name: department.name || t("settings_ui.unnamed_entity") })}
                                    onClick={() => openDepartmentEditor(index)}
                                  >
                                    {t("settings_ui.edit")}
                                    <IconChevronRight />
                                  </AppButton>
                                ) : null}
                              </div>
                            </header>

                            <div className="company-structure__shifts">
                              <div className="company-structure__shifts-header">
                                <span><IconTime aria-hidden="true" /> {t("settings_ui.structure_assigned_shifts")}</span>
                                <AppChip variant="muted" appearance="semiFilled" size="sm">
                                  {departmentShifts.length}
                                </AppChip>
                              </div>
                              {departmentShifts.length ? (
                                departmentShifts.map(({ shift, index: shiftIndex }) => {
                                  const otherDepartments = (shift.department_ids || [])
                                    .filter((departmentId) => departmentId !== department.id)
                                    .map((departmentId) => {
                                      const match = activeDepartmentOptions.find((item) => item.id === departmentId);
                                      return match?.shorthand_symbol || match?.name;
                                    })
                                    .filter(Boolean);

                                  return (
                                    <button
                                      type="button"
                                      className={`company-structure__shift${shift.is_active === false ? " is-inactive" : ""}`}
                                      key={`${department.id || index}-${shift.id || shift.client_id || shiftIndex}`}
                                      onClick={() => openShiftEditor(shiftIndex)}
                                      disabled={!canEditCompanySettings}
                                      style={{ "--company-shift-accent": shift.color_hex || "var(--ds-color-brand-default)" }}
                                    >
                                      <AppChip
                                        variant="primary"
                                        appearance="outlined"
                                        size="sm"
                                        className="company-structure__abbreviation-chip company-structure__abbreviation-chip--shift"
                                        style={{ "--company-entity-accent": shift.color_hex || "var(--ds-color-brand-default)" }}
                                      >
                                        {shift.shorthand_symbol || "—"}
                                      </AppChip>
                                      <span className="company-structure__shift-copy">
                                        <span className="company-structure__kind">{t("settings_ui.shift")}</span>
                                        <strong>{shift.name || t("settings_ui.unnamed_shift")}</strong>
                                        <span>
                                          {shift.start_time || "--:--"} – {shift.end_time || "--:--"} · {shiftBreakLabel(shift, t)}
                                        </span>
                                      </span>
                                      {otherDepartments.length || shift.is_active === false ? (
                                        <span className="company-structure__shift-tags">
                                          {otherDepartments.length ? (
                                            <AppChip variant="muted" appearance="semiFilled" size="sm">
                                              {t("settings_ui.structure_also_in_departments", { departments: otherDepartments.join(", ") })}
                                            </AppChip>
                                          ) : null}
                                          {shift.is_active === false ? (
                                            <AppChip variant="muted" appearance="outlined" size="sm">
                                              {t("settings_ui.inactive")}
                                            </AppChip>
                                          ) : null}
                                        </span>
                                      ) : null}
                                      <IconChevronRight className="company-structure__shift-chevron" aria-hidden="true" />
                                    </button>
                                  );
                                })
                              ) : (
                                <div className="company-structure__empty">
                                  <span>{t("settings_ui.structure_department_without_shifts")}</span>
                                  {canEditCompanySettings && department.is_active !== false ? (
                                    <AppButton variant="muted" size="sm" onClick={() => openDepartmentEditor(index)}>
                                      <IconAdd />
                                      {t("settings_ui.structure_assign_shifts")}
                                    </AppButton>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </section>
                        );
                      })
                    ) : (
                      <AppCallout
                        variant="info"
                        title={t("settings_ui.no_departments")}
                        description={t("settings_ui.no_departments_description")}
                      />
                    )}

                    {unassignedShiftRows.length ? (
                      <section className="company-structure__group company-structure__group--unassigned">
                        <header className="company-structure__group-header">
                          <span className="company-structure__unassigned-icon" aria-hidden="true"><IconWarning /></span>
                          <div className="company-structure__group-copy">
                            <span className="company-structure__kind">{t("settings_ui.structure_unassigned")}</span>
                            <strong>{t("settings_ui.structure_without_department")}</strong>
                            <span>{t("settings_ui.structure_unassigned_description")}</span>
                          </div>
                        </header>
                        <div className="company-structure__shifts">
                          <div className="company-structure__shifts-header">
                            <span><IconTime aria-hidden="true" /> {t("settings_ui.structure_unassigned_shifts")}</span>
                            <AppChip variant="muted" appearance="semiFilled" size="sm">
                              {unassignedShiftRows.length}
                            </AppChip>
                          </div>
                          {unassignedShiftRows.map(({ shift, index: shiftIndex }) => (
                            <button
                              type="button"
                              className={`company-structure__shift${shift.is_active === false ? " is-inactive" : ""}`}
                              key={shift.id || shift.client_id || shiftIndex}
                              onClick={() => openShiftEditor(shiftIndex)}
                              disabled={!canEditCompanySettings}
                              style={{ "--company-shift-accent": shift.color_hex || "var(--ds-color-brand-default)" }}
                            >
                              <AppChip
                                variant="primary"
                                appearance="outlined"
                                size="sm"
                                className="company-structure__abbreviation-chip company-structure__abbreviation-chip--shift"
                                style={{ "--company-entity-accent": shift.color_hex || "var(--ds-color-brand-default)" }}
                              >
                                {shift.shorthand_symbol || "—"}
                              </AppChip>
                              <span className="company-structure__shift-copy">
                                <span className="company-structure__kind">{t("settings_ui.shift")}</span>
                                <strong>{shift.name || t("settings_ui.unnamed_shift")}</strong>
                                <span>
                                  {shift.start_time || "--:--"} – {shift.end_time || "--:--"} · {shiftBreakLabel(shift, t)}
                                </span>
                              </span>
                              {shift.is_active === false ? (
                                <span className="company-structure__shift-tags">
                                  <AppChip variant="muted" appearance="outlined" size="sm">
                                    {t("settings_ui.inactive")}
                                  </AppChip>
                                </span>
                              ) : null}
                              <IconChevronRight className="company-structure__shift-chevron" aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      </section>
                    ) : null}
                  </div>
                </AppCardBody>
              </AppCard>
            ) : null}

            {activeSection === "time-off" ? (
              <AppCard>
                <AppCardHeader
                  title={t("settings_ui.time_off_types")}
                  subtitle={t("settings_ui.time_off_subtitle")}
                  visual={<IconHoliday />}
                />
                <AppCardBody>
                  <div className="company-time-off-types">
                    <TimeOffTypesSection
                      active={activeSection === "time-off"}
                      token={token}
                    />
                  </div>
                </AppCardBody>
              </AppCard>
            ) : null}

            {/* Ohne diese Karte war der Bereich "Öffnungstage" schlicht leer:
                Es gab nur den Bearbeiten-Dialog, aber keine Leseansicht. */}
            <CompanyCard
              hidden={activeSection !== "opening"}
              title={t("settings_ui.opening_days")}
              icon={<IconTimetable />}
              onManage={canEditCompanySettings ? () => openDrawer("opening") : undefined}
            >
              {(company.opening_days || []).length ? (
                <div className="company-opening-days-grid company-opening-days-grid--read">
                  {(company.opening_days || []).map((day) => (
                    <div
                      key={day.weekday}
                      className={`company-opening-day-readout${day.is_open ? " is-open" : ""}`}
                    >
                      <span className="company-opening-day-button__label company-opening-day-button__label--full">
                        {day.label}
                      </span>
                      <span
                        className="company-opening-day-button__label company-opening-day-button__label--short"
                        aria-hidden="true"
                      >
                        {String(day.label || "").slice(0, 2)}
                      </span>
                      <span className="company-opening-day-button__status">
                        {day.is_open ? "Geöffnet" : "Geschlossen"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <AppCallout
                  variant="warning"
                  description={t(
                    "settings_ui.opening_days_empty",
                    "Für diesen Standort sind noch keine Öffnungstage hinterlegt."
                  )}
                />
              )}
            </CompanyCard>

            <CompanyCard
              hidden={activeSection !== "solver"}
              title={t("settings_ui.solver_requirements")}
              icon={<IconAutoPlanning />}
              color="#0EA5E9"
              onManage={canEditCompanySettings && SOLVER_SETTINGS_EDITABLE ? () => openDrawer("solver") : undefined}
              actionColor="#2563EB"
            >
              {/* Ohne Bearbeiten-Knopf wären die alten Werte nur noch Zahlen ohne
                  Handlungsmöglichkeit - der Abschnitt sagt stattdessen, woran es liegt. */}
              {SOLVER_SETTINGS_EDITABLE ? (
                <AppFieldGrid columns={2}>
                  <ReadField label={t("settings_ui.daily_work_time")} value={`${solverRequirement.min_time_day ?? "-"} - ${solverRequirement.max_time_day ?? "-"} h`} />
                  <ReadField label={t("settings_ui.max_week")} value={`${solverRequirement.max_time_week ?? "-"} h`} />
                  <ReadField label={t("settings_ui.consecutive_working_days")} value={`${solverRequirement.subsequent_workingdays ?? "-"} / ${solverRequirement.subsequent_workingdays_max ?? "-"}`} />
                  <ReadField label={t("settings_ui.planning_period")} value={`${solverRequirement.week_timeframe || 1} ${t("settings_ui.weeks")}`} />
                  <ReadField label={t("settings_ui.save_draft")} value={solverRequirement.saving_draft ? t("settings_ui.yes") : t("settings_ui.no")} />
                </AppFieldGrid>
              ) : (
                <AppCallout
                  variant="info"
                  title={t("settings_ui.solver_maintenance_callout_title")}
                  description={t("settings_ui.solver_maintenance_callout_description")}
                />
              )}
            </CompanyCard>

          </div>
          )}

      {drawer && (
        <>
      <EditPanel
        title={t("settings_ui.edit_general_settings")}
        subtitle={t("settings_ui.edit_general_settings_subtitle")}
        open={drawer === "general"}
        onClose={closeDrawer}
        onSave={saveSection}
        saving={saving}
        dirty={hasAppValueChanged(initialForm, form)}
      >
        <AppFieldStack>
          {formError && <AppCallout variant="error" description={formError} />}
          <AppField label={t("settings_ui.company_name")} required>
            <AppInput
              value={form.company_name || ""}
              onChange={(event) => setForm((current) => ({ ...current, company_name: event.target.value }))}
            />
          </AppField>
          <AppFieldGrid columns={2}>
            <AppField label={t("settings_ui.weekly_hours")} required>
              <AppInput
                type="number"
                value={form.weekly_hours ?? ""}
                onChange={(event) => setForm((current) => ({ ...current, weekly_hours: event.target.value }))}
                min={0}
                step={0.25}
              />
            </AppField>
            <AppField label={t("settings_ui.working_days_per_week")} required>
              <AppInput
                type="number"
                value={form.working_days_per_week ?? ""}
                onChange={(event) => setForm((current) => ({ ...current, working_days_per_week: event.target.value }))}
                min={0}
                max={7}
              />
            </AppField>
          </AppFieldGrid>
        </AppFieldStack>
      </EditPanel>

      <EditPanel
        title={isNewDepartment
          ? t("settings_ui.add_department")
          : t("settings_ui.edit_named", { name: editedDepartment?.name || t("settings_ui.department") })}
        subtitle={isNewDepartment
          ? t("settings_ui.department_create_subtitle")
          : t("settings_ui.department_update_subtitle")}
        open={drawer === "departments"}
        onClose={closeDrawer}
        onSave={saveSection}
        saving={saving}
        dirty={hasAppValueChanged(initialForm, form)}
        saveLabel={isNewDepartment ? t("settings_ui.add_department") : undefined}
      >
        <AppFieldStack>
          {formError && <AppCallout variant="error" description={formError} />}
          {editedDepartment ? (
            <>
              <section className="company-structure-editor__section">
                <div className="company-structure-editor__section-copy">
                  <AppText as="h4" variant="heading-4">{t("settings_ui.basic_data")}</AppText>
                  <AppText variant="body-2" tone="secondary">{t("settings_ui.department_basic_data_description")}</AppText>
                </div>
                <AppFieldGrid columns={2}>
                  <AppField label={t("settings_ui.name")} required>
                    <AppInput value={editedDepartment.name || ""} onChange={(event) => updateDepartment(departmentEditIndex, "name", event.target.value)} />
                  </AppField>
                  <AppField label={t("settings_ui.abbreviation")} required>
                    <AppInput
                      value={editedDepartment.shorthand_symbol || ""}
                      maxLength={SHORTHAND_MAX_LENGTH}
                      onChange={(event) => updateDepartment(departmentEditIndex, "shorthand_symbol", normalizeShorthand(event.target.value))}
                    />
                  </AppField>
                </AppFieldGrid>
                <AppField label={t("settings_ui.color")}>
                  <SettingsColorPicker
                    value={editedDepartment.color_hex}
                    label={editedDepartment.name || t("settings_ui.department")}
                    palette={colorPalette}
                    onChange={(color) => updateDepartment(departmentEditIndex, "color_hex", color)}
                  />
                </AppField>
              </section>

              <section className="company-structure-editor__section">
                <div className="company-structure-editor__section-copy">
                  <AppText as="h4" variant="heading-4">{t("settings_ui.assignments")}</AppText>
                  <AppText variant="body-2" tone="secondary">{t("settings_ui.department_assignments_description")}</AppText>
                </div>
                <AppField label={t("settings_ui.employees_count", { count: editedDepartment.user_ids?.length || 0 })}>
                  <AppSelect
                    options={currentUsers.map((user) => ({ value: user.id, label: user.name || user.email || `User ${user.id}` }))}
                    multiple
                    searchable
                    showSelectAll
                    value={editedDepartment.user_ids || []}
                    onChange={(event) => updateDepartment(departmentEditIndex, "user_ids", event.target.value)}
                    disabled={editedDepartment.is_active === false || currentUsers.length === 0}
                    placeholder={t("settings_ui.add_person")}
                    searchPlaceholder={t("settings_ui.search_person")}
                  />
                </AppField>
                <AppField
                  label={t("settings_ui.assigned_shifts_count", { count: editedDepartment.shift_ids?.length || 0 })}
                  help={t("settings_ui.department_shifts_help")}
                >
                  <div className="company-structure-editor__selection-list">
                    <AppSelectionList
                      items={activeShifts.map((shift) => ({
                        ...shift,
                        disabled: editedDepartment.is_active === false,
                      }))}
                      value={editedDepartment.shift_ids || []}
                      onChange={(shiftIds) => updateDepartment(departmentEditIndex, "shift_ids", shiftIds)}
                      getItemKey={(shift) => shift.id}
                      getItemLabel={(shift) => shift.name || t("settings_ui.unnamed_shift")}
                      getItemDescription={(shift) => shiftScheduleLabel(shift, t)}
                      renderLeading={(shift) => (
                        <AppChip
                          variant="primary"
                          appearance="outlined"
                          size="sm"
                          className="company-structure__abbreviation-chip company-structure__abbreviation-chip--shift"
                          style={{ "--company-entity-accent": shift.color_hex || "var(--ds-color-brand-default)" }}
                        >
                          {shift.shorthand_symbol || "—"}
                        </AppChip>
                      )}
                      emptyState={t("settings_ui.no_active_shifts")}
                      aria-label={t("settings_ui.assign_shifts_aria")}
                    />
                  </div>
                </AppField>
              </section>

              {!isNewDepartment ? (
                <div className="company-location-status-field">
                  <div className="company-location-status-field__copy">
                    <strong>{editedDepartment.is_active !== false
                      ? t("settings_ui.department_active_title")
                      : t("settings_ui.department_inactive_title")}</strong>
                    <span>
                      {editedDepartment.is_active !== false
                        ? t("settings_ui.department_active_description")
                        : t("settings_ui.department_inactive_description")}
                    </span>
                  </div>
                  <AppRadioGroup
                    direction="row"
                    name="department-activity"
                    options={[
                      { value: "active", label: t("settings_ui.active") },
                      { value: "inactive", label: t("settings_ui.inactive") },
                    ]}
                    value={editedDepartment.is_active !== false ? "active" : "inactive"}
                    onChange={(event) => requestActiveChange("departments", departmentEditIndex, event.target.value === "active")}
                    aria-label={t("settings_ui.department_activity_aria")}
                  />
                </div>
              ) : null}
            </>
          ) : <AppCallout variant="warning" description={t("settings_ui.department_unavailable")} />}
        </AppFieldStack>
      </EditPanel>

      <EditPanel
        title={t("settings_ui.edit_opening_days")}
        subtitle={t("settings_ui.edit_opening_days_subtitle")}
        open={drawer === "opening"}
        onClose={closeDrawer}
        onSave={saveSection}
        saving={saving}
        dirty={hasAppValueChanged(initialForm, form)}
      >
        <AppFieldStack>
          {formError && <AppCallout variant="error" description={formError} />}
          <div className="company-opening-days-grid">
            {(form.opening_days || []).map((day, index) => (
              <AppButton
                key={day.weekday}
                variant={day.is_open ? "primary" : "outlined"}
                size="sm"
                className="company-opening-day-button"
                aria-pressed={day.is_open}
                aria-label={`${day.label}: ${day.is_open ? "Geöffnet" : "Geschlossen"}`}
                onClick={() => toggleOpeningDay(index)}
              >
                <span className="company-opening-day-button__label company-opening-day-button__label--full">
                  {day.label}
                </span>
                <span className="company-opening-day-button__label company-opening-day-button__label--short" aria-hidden="true">
                  {day.label.slice(0, 2)}
                </span>
                <span className="company-opening-day-button__status">
                  {day.is_open ? "Geöffnet" : "Geschlossen"}
                </span>
              </AppButton>
            ))}
          </div>

          {closedOpeningWeekdays.length ? (
            <AppCallout
              variant="warning"
              title={t("settings_ui.opening_days_closed_title")}
              description={t("settings_ui.opening_days_closed_info", {
                days: closedOpeningWeekdays.map(weekdayName).join(", "),
              })}
            />
          ) : null}

          {reopenedOpeningWeekdays.length ? (
            <div className="company-opening-availability">
              <AppText as="h4" variant="heading-4">
                {t("settings_ui.opening_days_reopened_title")}
              </AppText>
              <AppText variant="body-2" tone="secondary">
                {t("settings_ui.opening_days_reopened_info")}
              </AppText>
              {reopenedOpeningWeekdays.map((weekday) => {
                // Ohne eigene Auswahl gilt der Vorschlag — genau der wird auch
                // gespeichert, die Zahl hier zeigt also den echten Stand.
                const selected = form.opening_availability?.[weekday]
                  || suggestedAvailableUserIds(weekday);
                return (
                  <div className="company-opening-availability__row" key={weekday}>
                    <div className="company-opening-availability__copy">
                      <AppText variant="label-1">{weekdayName(weekday)}</AppText>
                      <AppText variant="body-2" tone="secondary">
                        {t("settings_ui.opening_days_available_count", {
                          count: selected.length,
                          total: currentUsers.length,
                        })}
                      </AppText>
                    </div>
                    <AppButton variant="neutral" size="sm" onClick={() => openOpeningDayDialog(weekday)}>
                      <IconEdit />
                      {t("settings_ui.opening_days_edit_available")}
                    </AppButton>
                  </div>
                );
              })}
            </div>
          ) : null}
        </AppFieldStack>
      </EditPanel>

      <AppDialog
        open={Boolean(openingDayDialog)}
        onClose={closeOpeningDayDialog}
        size="sm"
      >
        <AppDialogHeader
          title={t("settings_ui.opening_days_dialog_title", {
            day: weekdayName(openingDayDialog?.weekday),
          })}
          subtitle={t("settings_ui.opening_days_dialog_subtitle")}
          visual={<IconTimetable />}
        />
        <AppDialogBody>
          {currentUsers.length ? (
            <AppFieldStack>
              <div className="company-opening-availability__actions">
                <AppButton
                  variant="neutral"
                  size="sm"
                  onClick={() =>
                    setOpeningDayDialog((current) => ({
                      ...current,
                      userIds: currentUsers.map((item) => item.id),
                    }))
                  }
                >
                  {t("settings_ui.opening_days_select_all")}
                </AppButton>
                <AppButton
                  variant="neutral"
                  size="sm"
                  onClick={() => setOpeningDayDialog((current) => ({ ...current, userIds: [] }))}
                >
                  {t("settings_ui.opening_days_select_none")}
                </AppButton>
              </div>
              <div className="company-opening-availability__users">
                {currentUsers.map((item) => (
                  <AppCheckbox
                    key={item.id}
                    label={item.name || item.email}
                    labelVariant="body-1"
                    checked={Boolean(openingDayDialog?.userIds?.includes(item.id))}
                    onChange={() => toggleOpeningDayUser(item.id)}
                  />
                ))}
              </div>
            </AppFieldStack>
          ) : (
            <AppCallout variant="info" description={t("settings_ui.opening_days_no_users")} />
          )}
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" size="md" onClick={closeOpeningDayDialog}>
            {t("button.cancel")}
          </AppButton>
          <AppButton variant="primary" size="md" onClick={confirmOpeningDayDialog}>
            {t("settings_ui.opening_days_dialog_confirm")}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>

      <EditPanel
        title={isNewShift
          ? t("settings_ui.add_shift")
          : t("settings_ui.edit_named", { name: editedShift?.name || t("settings_ui.shift") })}
        subtitle={isNewShift
          ? t("settings_ui.shift_create_subtitle")
          : t("settings_ui.shift_update_subtitle")}
        open={drawer === "shifts"}
        onClose={closeDrawer}
        onSave={saveSection}
        saving={saving}
        dirty={hasAppValueChanged(initialForm, form)}
        saveLabel={isNewShift ? t("settings_ui.add_shift") : undefined}
      >
        <AppFieldStack>
          {formError && <AppCallout variant="error" description={formError} />}
          {editedShift ? (
            <>
              <AppFieldGrid columns={2}>
                <AppField label={t("settings_ui.name")} required><AppInput value={editedShift.name || ""} onChange={(event) => updateShift(shiftEditIndex, "name", event.target.value)} /></AppField>
                <AppField label={t("settings_ui.abbreviation")} required><AppInput maxLength={SHORTHAND_MAX_LENGTH} value={editedShift.shorthand_symbol || ""} onChange={(event) => updateShift(shiftEditIndex, "shorthand_symbol", event.target.value)} /></AppField>
              </AppFieldGrid>
              <AppField label={t("settings_ui.description")}><AppInput value={editedShift.description || ""} onChange={(event) => updateShift(shiftEditIndex, "description", event.target.value)} /></AppField>
              <AppFieldGrid columns={2} className="company-color-assignment-grid">
                <AppField label={t("settings_ui.color")}>
                  <SettingsColorPicker
                    value={editedShift.color_hex}
                    label={editedShift.name || t("settings_ui.shift")}
                    palette={colorPalette}
                    onChange={(color) => updateShift(shiftEditIndex, "color_hex", color)}
                  />
                </AppField>
                <AppField
                  label={t("settings_ui.departments")}
                  required
                  error={
                    editedShift.is_active !== false && !(editedShift.department_ids || []).length
                      ? t("initial_setup.department_required")
                      : undefined
                  }
                >
                  <AppSelect
                    options={activeDepartmentOptions.map((department) => ({ value: department.id, label: department.name }))}
                    multiple
                    searchable
                    showSelectAll
                    invalid={editedShift.is_active !== false && !(editedShift.department_ids || []).length}
                    value={editedShift.department_ids || []}
                    onChange={(event) => updateShift(shiftEditIndex, "department_ids", event.target.value)}
                    disabled={editedShift.is_active === false}
                    placeholder={t("settings_ui.add_department")}
                  />
                </AppField>
              </AppFieldGrid>
              <AppFieldGrid columns={3}>
                <AppField label={t("settings_ui.start")} required error={editedShift.start_time && !TIME_RE.test(editedShift.start_time) ? t("settings_ui.time_format_error") : undefined}>
                  <AppInput maxLength={5} inputMode="numeric" placeholder="HH:MM" invalid={Boolean(editedShift.start_time) && !TIME_RE.test(editedShift.start_time)} value={editedShift.start_time || ""} onChange={(event) => updateShift(shiftEditIndex, "start_time", event.target.value)} />
                </AppField>
                <AppField label={t("settings_ui.end")} required error={editedShift.end_time && !TIME_RE.test(editedShift.end_time) ? t("settings_ui.time_format_error") : undefined}>
                  <AppInput maxLength={5} inputMode="numeric" placeholder="HH:MM" invalid={Boolean(editedShift.end_time) && !TIME_RE.test(editedShift.end_time)} value={editedShift.end_time || ""} onChange={(event) => updateShift(shiftEditIndex, "end_time", event.target.value)} />
                </AppField>
                <AppField label={t("settings_ui.break_type")}>
                  <AppSelect
                    options={[
                      { value: "", label: t("settings_ui.break_type_none") },
                      { value: "duration", label: t("settings_ui.break_type_duration") },
                      { value: "fixed", label: t("settings_ui.break_type_fixed") },
                    ]}
                    value={editedShift.break_type || ""}
                    onChange={(event) => updateShift(shiftEditIndex, "break_type", event.target.value)}
                  />
                </AppField>
              </AppFieldGrid>
              {editedShift.break_type === "duration" ? (
                <AppFieldGrid columns={3}>
                  <AppField label={t("settings_ui.break_minutes")}>
                    <AppInput inputMode="numeric" value={editedShift.break_duration_minutes ?? ""} onChange={(event) => updateShift(shiftEditIndex, "break_duration_minutes", event.target.value.replace(/[^\d]/g, "").slice(0, 3))} />
                  </AppField>
                </AppFieldGrid>
              ) : null}
              {editedShift.break_type === "fixed" ? (
                <AppFieldGrid columns={3}>
                  <AppField label={t("settings_ui.break_start")} required error={editedShift.break_start_time && !TIME_RE.test(editedShift.break_start_time) ? t("settings_ui.time_format_error") : undefined}>
                    <AppInput maxLength={5} inputMode="numeric" placeholder="HH:MM" invalid={Boolean(editedShift.break_start_time) && !TIME_RE.test(editedShift.break_start_time)} value={editedShift.break_start_time || ""} onChange={(event) => updateShift(shiftEditIndex, "break_start_time", event.target.value)} />
                  </AppField>
                  <AppField label={t("settings_ui.break_end")} required error={editedShift.break_end_time && !TIME_RE.test(editedShift.break_end_time) ? t("settings_ui.time_format_error") : undefined}>
                    <AppInput maxLength={5} inputMode="numeric" placeholder="HH:MM" invalid={Boolean(editedShift.break_end_time) && !TIME_RE.test(editedShift.break_end_time)} value={editedShift.break_end_time || ""} onChange={(event) => updateShift(shiftEditIndex, "break_end_time", event.target.value)} />
                  </AppField>
                </AppFieldGrid>
              ) : null}
              {!isNewShift ? (
                <div className="company-location-status-field">
                  <div className="company-location-status-field__copy">
                    <strong>{editedShift.is_active !== false
                      ? t("settings_ui.shift_active_title")
                      : t("settings_ui.shift_inactive_title")}</strong>
                    <span>
                      {editedShift.is_active !== false
                        ? t("settings_ui.shift_active_description")
                        : t("settings_ui.shift_inactive_description")}
                    </span>
                  </div>
                  <AppRadioGroup
                    direction="row"
                    name="shift-activity"
                    options={[
                      { value: "active", label: t("settings_ui.active") },
                      { value: "inactive", label: t("settings_ui.inactive") },
                    ]}
                    value={editedShift.is_active !== false ? "active" : "inactive"}
                    onChange={(event) => requestActiveChange("shifts", shiftEditIndex, event.target.value === "active")}
                    aria-label={t("settings_ui.shift_activity_aria")}
                  />
                </div>
              ) : null}
            </>
          ) : <AppCallout variant="warning" description={t("settings_ui.shift_unavailable")} />}
        </AppFieldStack>
      </EditPanel>

      {/* Erreichbar erst wieder, wenn SOLVER_SETTINGS_EDITABLE gesetzt ist -
          der Abschnitt bietet solange kein Bearbeiten an. */}
      <EditPanel
        title={t("settings_ui.edit_solver_requirements")}
        subtitle={t("settings_ui.edit_solver_requirements_subtitle")}
        open={drawer === "solver"}
        onClose={closeDrawer}
        onSave={saveSection}
        saving={saving}
        dirty={hasAppValueChanged(initialForm, form)}
      >
        <Stack spacing={2.5}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <Box sx={solverSectionSx}>
            <Typography variant="subtitle1" fontWeight={800}>
              Allgemein
            </Typography>
            <Box sx={solverGridSx}>
              <FormControl fullWidth>
                <InputLabel id="solver-week-timeframe">{t("settings_ui.planning_period")}</InputLabel>
                <Select
                  labelId="solver-week-timeframe"
                  label={t("settings_ui.planning_period")}
                  value={String(form.solver_requirement?.week_timeframe ?? 1)}
                  onChange={(event) => updateSolverRequirement("week_timeframe", event.target.value)}
                >
                  <MenuItem value="1">1 Woche</MenuItem>
                  <MenuItem value="2">2 Wochen</MenuItem>
                  <MenuItem value="4">4 Wochen</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="solver-fair-temp">{t("settings_ui.fair_distribution")}</InputLabel>
                <Select
                  labelId="solver-fair-temp"
                  label={t("settings_ui.fair_distribution")}
                  value={form.solver_requirement?.fair_distribution_temp || "woche"}
                  onChange={(event) => updateSolverRequirement("fair_distribution_temp", event.target.value)}
                >
                  <MenuItem value="woche">{t("settings_ui.per_week")}</MenuItem>
                  <MenuItem value="berechnungszeitraum">{t("settings_ui.calculation_period")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="solver-daily-deployment">{t("settings_ui.deployments_per_day")}</InputLabel>
                <Select
                  labelId="solver-daily-deployment"
                  label={t("settings_ui.deployments_per_day")}
                  value={String(form.solver_requirement?.daily_deployment ?? 1)}
                  onChange={(event) => updateSolverRequirement("daily_deployment", event.target.value)}
                >
                  <MenuItem value="1">1</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="solver-skills-day">{t("settings_ui.skills_per_day")}</InputLabel>
                <Select
                  labelId="solver-skills-day"
                  label={t("settings_ui.skills_per_day")}
                  value={String(form.solver_requirement?.skills_per_day ?? 1)}
                  onChange={(event) => updateSolverRequirement("skills_per_day", event.target.value)}
                >
                  <MenuItem value="1">{t("settings_ui.yes")}</MenuItem>
                  <MenuItem value="0">{t("settings_ui.no")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="solver-weekend-fair">{t("settings_ui.fair_weekends")}</InputLabel>
                <Select
                  labelId="solver-weekend-fair"
                  label={t("settings_ui.fair_weekends")}
                  value={String(form.solver_requirement?.fair_distribution_weekend ?? 0)}
                  onChange={(event) => updateSolverRequirement("fair_distribution_weekend", event.target.value)}
                >
                  <MenuItem value="1">{t("settings_ui.yes")}</MenuItem>
                  <MenuItem value="0">{t("settings_ui.no")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="solver-always-available">{t("settings_ui.always_available")}</InputLabel>
                <Select
                  labelId="solver-always-available"
                  label={t("settings_ui.always_available")}
                  value={String(form.solver_requirement?.perm_always_available ?? 0)}
                  onChange={(event) => updateSolverRequirement("perm_always_available", event.target.value)}
                >
                  <MenuItem value="1">{t("settings_ui.yes")}</MenuItem>
                  <MenuItem value="0">{t("settings_ui.no")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="solver-saving-draft">{t("settings_ui.save_draft")}</InputLabel>
                <Select
                  labelId="solver-saving-draft"
                  label={t("settings_ui.save_draft")}
                  value={form.solver_requirement?.saving_draft ? "1" : "0"}
                  onChange={(event) => updateSolverRequirement("saving_draft", event.target.value === "1")}
                >
                  <MenuItem value="1">{t("settings_ui.yes")}</MenuItem>
                  <MenuItem value="0">{t("settings_ui.no")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box sx={solverSectionSx}>
            <Typography variant="subtitle1" fontWeight={800}>
              Arbeitszeitregeln
            </Typography>
            <Box sx={solverGridSx}>
              {solverWorkFields.map(solverNumberField)}
            </Box>
          </Box>

          <Box sx={solverSectionSx}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={800}>
                Profil
              </Typography>
              <Typography color="text.secondary">
                Wähle ein Solver-Profil. Das Profil setzt die Gewichtungen automatisch.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant={solverStandardSelected ? "contained" : "outlined"}
                startIcon={solverStandardSelected ? <CheckCircleOutlineIcon /> : null}
                onClick={() => applySolverProfile("standard")}
                sx={solverProfileButtonSx}
              >
                Standard
              </Button>
            </Stack>
          </Box>
        </Stack>
      </EditPanel>

        </>
      )}
            </main>
          </div>
        </>
      )}

      </AppWorkspace>

      <AppDialog open={Boolean(inactiveConfirm)} onClose={() => setInactiveConfirm(null)} size="sm">
        <AppDialogHeader
          title={INACTIVE_COPY[inactiveConfirm?.type]?.title || "Eintrag deaktivieren?"}
          visual={<IconWarning />}
        />
        <AppDialogBody>
          <AppFieldStack>
            <AppText as="p" variant="body1">
              Möchtest du {INACTIVE_COPY[inactiveConfirm?.type]?.label || "diesen Eintrag"}{" "}
              <strong>{inactiveConfirm?.name}</strong> wirklich deaktivieren?
            </AppText>
            <AppCallout
              variant="warning"
              description={INACTIVE_COPY[inactiveConfirm?.type]?.message || "Der Eintrag ist danach nicht mehr auswählbar."}
            />
          </AppFieldStack>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" size="md" onClick={() => setInactiveConfirm(null)}>
            Abbrechen
          </AppButton>
          <AppButton variant="error" size="md" onClick={confirmInactive}>
            Deaktivieren
          </AppButton>
        </AppDialogFooter>
      </AppDialog>
    </div>
  );
}

const solverSectionSx = {
  pt: 2.5,
  borderTop: "1px solid",
  borderColor: "divider",
  display: "grid",
  gap: 2,
  "&:first-of-type": {
    pt: 0,
    borderTop: 0,
  },
};

const solverProfileButtonSx = {
  minHeight: 44,
  borderRadius: 1.5,
  px: 2,
  fontWeight: 850,
  textTransform: "none",
};

const solverGridSx = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
};
